import { Checkbox, Empty, Flex, Skeleton, Typography } from 'antd'
import { DrilldownDataContext } from 'features/drilldown/data/savedExplorationState/DrilldownExplorationContext'
import { useNiiPdfExportScope } from 'features/drilldown/panels/impl/niiPdfExport/useNiiPdfExportScope'
import {
  excludedFromSelectedColumns,
  isNiiSheetIncluded,
  NII_SHEET_DESCRIPTIONS,
  NII_SHEET_KEYS,
  NII_SHEET_LABELS,
  NII_SHEET_REQUIRED_COLUMNS,
  type NiiSheetKey,
  selectedNiiSheetColumns,
} from 'features/libra/stepComponents/niiReport/sheet/niiSheetSelection'
import { useBulkNiiSheetExport } from 'features/libra/stepComponents/niiReport/sheet/useBulkNiiSheetExport'
import { type FC, useContext } from 'react'

import { NiiSheetColumnPicker } from './NiiSheetColumnPicker'
import { useNiiReportSheetSelection } from './useNiiReportSheetSelection'

const { Text } = Typography

/**
 * Column picker for the NII spreadsheet export, shown in the panel's config
 * drawer.
 *
 * Columns come from the same built rows the export writes, so the list is
 * exactly what the current scope can produce rather than a hardcoded guess.
 */
export const NiiReportSheetExportConfigView: FC = () => {
  const { orgId } = useContext(DrilldownDataContext)
  const { selection, setSelection, includeIncomplete, setIncludeIncomplete } =
    useNiiReportSheetSelection()
  const { tickets, workflowMap, isLoading } = useNiiPdfExportScope({
    includeIncomplete,
  })
  const { columnsBySheet } = useBulkNiiSheetExport(
    tickets,
    workflowMap,
    orgId ?? '',
    selection
  )

  const setSheetIncluded = (sheet: NiiSheetKey, included: boolean): void => {
    setSelection({
      ...selection,
      excludedSheets: included
        ? selection.excludedSheets.filter((key) => key !== sheet)
        : [...selection.excludedSheets.filter((key) => key !== sheet), sheet],
    })
  }

  const setSheetColumns = (
    sheet: NiiSheetKey,
    nextColumns: ReadonlyArray<string>
  ): void => {
    setSelection({
      ...selection,
      excludedColumns: {
        ...selection.excludedColumns,
        [sheet]: excludedFromSelectedColumns(
          sheet,
          columnsBySheet[sheet],
          nextColumns
        ),
      },
    })
  }

  // Shown even with no tickets, so a scope with only in-progress tickets can
  // still be opted in.
  const includeIncompleteCheckbox = (
    <Checkbox
      checked={includeIncomplete}
      onChange={(event) => setIncludeIncomplete(event.target.checked)}>
      Include in-progress tickets
    </Checkbox>
  )

  if (isLoading) return <Skeleton active paragraph={{ rows: 4 }} />

  if (tickets.length === 0) {
    return (
      <Flex vertical gap='middle'>
        {includeIncompleteCheckbox}
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='No NII reports in the current scope to configure'
        />
      </Flex>
    )
  }

  return (
    <Flex vertical gap='middle'>
      {includeIncompleteCheckbox}
      <Text type='secondary'>
        Choose what the spreadsheet includes. Key columns stay so rows can be
        traced back — uncheck a whole sheet to drop those too.
      </Text>

      {NII_SHEET_KEYS.map((sheet) => (
        <NiiSheetColumnPicker
          key={sheet}
          label={NII_SHEET_LABELS[sheet]}
          description={NII_SHEET_DESCRIPTIONS[sheet]}
          columns={columnsBySheet[sheet]}
          requiredColumns={NII_SHEET_REQUIRED_COLUMNS[sheet]}
          included={isNiiSheetIncluded(sheet, selection)}
          selectedColumns={selectedNiiSheetColumns(
            sheet,
            columnsBySheet[sheet],
            selection
          )}
          onIncludedChange={(included) => setSheetIncluded(sheet, included)}
          onSelectedColumnsChange={(columns) => setSheetColumns(sheet, columns)}
        />
      ))}
    </Flex>
  )
}
